import { api } from "@/lib/apiClient";
import { readGuestTabletWorkstationCode } from "@/lib/guestTabletWorkstationStorage";
import { grossAmountFromNet } from "@/lib/money";
import type { GuestTabletSessionDisplay } from "@/types/guestTablet";

export type PosCartPhase = "open" | "cash" | "card" | "paid";

export type PosCartSyncLine = {
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
};

export type PosCartSyncPayload = {
  bookingId?: string | null;
  guestDisplayName: string;
  lines: PosCartSyncLine[];
  totalAmount: number;
  currency?: string;
  phase?: PosCartPhase;
  highlightIndex?: number;
};

function cartSignature(payload: PosCartSyncPayload | null): string {
  if (!payload) return CLEARED_SIGNATURE;
  return JSON.stringify({
    bookingId: payload.bookingId ?? null,
    guestDisplayName: payload.guestDisplayName,
    phase: payload.phase ?? "open",
    highlightIndex: payload.highlightIndex ?? null,
    lines: payload.lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      lineTotal: l.lineTotal,
    })),
    totalAmount: payload.totalAmount,
  });
}

/** Server-side cart is empty; distinct from "" so clears still run after resetPosCartSyncSignature(). */
const CLEARED_SIGNATURE = "__cleared__";

let lastSyncedSignature = "";

export function buildPosCartSyncPayload(
  guest: { bookingId: string; guestName: string } | null,
  cart: Array<{
    article: { name: string; current_price: string | null; tax_rate_percent: string | number };
    quantity: number;
  }>,
  options?: {
    phase?: PosCartPhase;
    walkIn?: boolean;
    highlightIndex?: number;
  },
): PosCartSyncPayload | null {
  if (cart.length === 0) return null;
  if (!guest && !options?.walkIn) return null;

  const lines: PosCartSyncLine[] = [];
  let total = 0;
  for (const line of cart) {
    const net = line.article.current_price;
    if (net == null) continue;
    const grossUnit =
      grossAmountFromNet(net, line.article.tax_rate_percent) ?? 0;
    const unitPrice = Math.round(grossUnit * 100) / 100;
    const lineTotal = Math.round(unitPrice * line.quantity * 100) / 100;
    total += lineTotal;
    lines.push({
      description: line.article.name,
      quantity: line.quantity,
      unitPrice,
      lineTotal,
    });
  }
  if (lines.length === 0) return null;

  const highlightIndex =
    options?.highlightIndex != null &&
    options.highlightIndex >= 0 &&
    options.highlightIndex < lines.length
      ? options.highlightIndex
      : lines.length - 1;

  return {
    bookingId: guest?.bookingId ?? null,
    guestDisplayName: guest?.guestName ?? "Laufkunde",
    lines,
    totalAmount: Math.round(total * 100) / 100,
    currency: "EUR",
    phase: options?.phase ?? "open",
    highlightIndex,
  };
}

export async function syncGuestTabletPosCart(
  payload: PosCartSyncPayload | null,
): Promise<GuestTabletSessionDisplay | null> {
  const workstationCode = readGuestTabletWorkstationCode();
  if (!workstationCode) return null;

  const signature = cartSignature(payload);
  if (signature === lastSyncedSignature) return null;

  if (!payload) {
    const session = await api.put<GuestTabletSessionDisplay>(
      `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}/pos-cart`,
      { lines: [], total_amount: "0" },
    );
    lastSyncedSignature = CLEARED_SIGNATURE;
    return session;
  }

  const session = await api.put<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}/pos-cart`,
    {
      ...(payload.bookingId ? { booking_id: payload.bookingId } : {}),
      guest_display_name: payload.guestDisplayName,
      lines: payload.lines.map((line) => ({
        description: line.description,
        quantity: String(line.quantity),
        unit_price: line.unitPrice.toFixed(2),
        line_total: line.lineTotal.toFixed(2),
      })),
      total_amount: payload.totalAmount.toFixed(2),
      currency: payload.currency ?? "EUR",
      phase: payload.phase ?? "open",
      highlight_index: payload.highlightIndex ?? payload.lines.length - 1,
    },
  );
  lastSyncedSignature = signature;
  return session;
}

export function resetPosCartSyncSignature(): void {
  lastSyncedSignature = "";
}
